import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { WettkampfSignaturen } from '../api/apiClient';
import { WETTKAMPF_STATUS_BADGE_CLASS, WETTKAMPF_STATUS_LABELS } from '../constants';
import { formatDatum } from '../utils/format';

const STATUS_ANGEFORDERT = 'unterschrift';
const LISTE_ID = 'ausstehende-signaturen-liste';

function ChevronIcon({ open }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

const istAngefordert = (item) => item?.status === STATUS_ANGEFORDERT;

/** Angeforderte Unterschriften zuerst, danach nach Datum (ältestes zuerst) und Wettkampfnummer. */
function vergleicheSignaturen(a, b) {
  return (Number(istAngefordert(b)) - Number(istAngefordert(a)))
    || String(a.datum ?? '9999').localeCompare(String(b.datum ?? '9999'))
    || Number(a.nummer ?? 0) - Number(b.nummer ?? 0);
}

function SignaturZeile({ item }) {
  const angefordert = istAngefordert(item);
  const link = item.saisonId != null && item.nummer != null
    ? `/saison/${item.saisonId}/wettkampf/${item.nummer}/ergebnis`
    : null;
  const statusLabel = angefordert
    ? 'Unterschrift angefordert'
    : WETTKAMPF_STATUS_LABELS[item.status] ?? item.status;
  const statusClass = angefordert ? 'badge-amber' : WETTKAMPF_STATUS_BADGE_CLASS[item.status] ?? 'badge-gray';

  const className = `block border-l-4 px-4 py-3 ${angefordert ? 'border-amber-400 bg-amber-50' : 'border-transparent'}`
    + (link ? ` no-underline transition-colors ${angefordert ? 'hover:bg-amber-100' : 'hover:bg-slate-50'}` : '');
  const inhalt = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm text-slate-500">
          <span className="font-medium text-slate-900">{item.eigeneMannschaft}</span>
          {item.gegnerMannschaft && ` gegen ${item.gegnerMannschaft}`}
        </span>
        <span className={`badge ${statusClass} shrink-0`}>{statusLabel}</span>
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {formatDatum(item.datum)}
        {item.nummer != null && ` · Wettkampf Nr. ${item.nummer}`}
        {item.runde != null && ` · Runde ${item.runde}`}
        {item.seite === 'heim' ? ' · Heim' : item.seite === 'auswaerts' ? ' · Auswärts' : ''}
      </div>
    </>
  );

  return (
    <li>
      {link ? <Link to={link} className={className}>{inhalt}</Link> : <div className={className}>{inhalt}</div>}
    </li>
  );
}

/**
 * Dashboard-Liste der Wettkämpfe der aktiven Saison mit eingetragenem
 * Ergebnis, bei denen der Schütze Mitglied einer beteiligten Mannschaft ist
 * und für deren Seite noch keine Unterschrift vorliegt. Wettkämpfe, bei denen
 * die Unterschrift gerade angefordert wird (Status „unterschrift“), sind
 * farblich hervorgehoben und stehen oben; bereits abgeschlossene lassen sich
 * noch nachträglich signieren.
 *
 * Die Liste ist einklappbar und nur sichtbar, solange etwas aussteht. Sie
 * startet aufgeklappt, sobald eine Unterschrift angefordert ist – sonst
 * eingeklappt. Ladefehler bleiben bewusst stumm (wie beim Login-Hinweis).
 */
function AusstehendeSignaturenListe({ schuetzenId }) {
  const [items, setItems] = useState([]);
  // null = noch keine Nutzerwahl, dann gilt der Standard (siehe unten).
  const [nutzerWahl, setNutzerWahl] = useState(null);

  useEffect(() => {
    if (!Number.isFinite(schuetzenId) || schuetzenId <= 0) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    WettkampfSignaturen.getAusstehendAktuelleSaison()
      .then((data) => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [schuetzenId]);

  const sortiert = useMemo(() => [...items].sort(vergleicheSignaturen), [items]);
  const anzahlAngefordert = useMemo(() => items.filter(istAngefordert).length, [items]);

  if (items.length === 0) return null;

  const aufgeklappt = nutzerWahl ?? anzahlAngefordert > 0;

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setNutzerWahl(!aufgeklappt)}
        aria-expanded={aufgeklappt}
        aria-controls={LISTE_ID}
        className="card-header w-full text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronIcon open={aufgeklappt} />
          <h3 className="font-semibold text-slate-800">Ausstehende Unterschriften</h3>
          <span className={anzahlAngefordert > 0 ? 'badge-amber' : 'badge-gray'}>{items.length}</span>
        </div>
        {anzahlAngefordert > 0 && (
          <span className="text-xs font-medium text-amber-700 shrink-0">{anzahlAngefordert} angefordert</span>
        )}
      </button>
      {aufgeklappt && (
        <ul id={LISTE_ID} className="divide-y divide-slate-100">
          {sortiert.map((item) => <SignaturZeile key={`${item.wettkampfId}-${item.seite}`} item={item} />)}
        </ul>
      )}
    </div>
  );
}

export default AusstehendeSignaturenListe;
